import { commonUtils } from '@/helpers/commonUtils'
import { Popover, PopoverProps } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import React, { CSSProperties, useCallback, useMemo, useState } from 'react'
import style from './UserInfoPopover.module.less'

import { toastController } from '@/controllers/toastController'
import { userService } from '@/services/userService'
import { SvgIcon } from '../SvgIcon/SvgIcon'

import { Link } from 'react-router-dom'
import {
  AdvancedModelTip,
  BaseModelTip,
  ExpiredTip,
  ImageTip,
  PdfProTip,
  PdfUltraTip,
  UltraModelTip,
} from '../ModelTip/ModelTip'
import { UpgradeLinkBtn } from '../UpgradeLinkBtn/UpgradeLinkBtn'

export interface UserInfoPopoverProps extends PopoverProps {
  className?: string
  customStyles?: CSSProperties
  children?: React.ReactNode
  displayType: 'popover' | 'normal'
  showLogout?: boolean
}

export const UserInfoPopover = observer((props: UserInfoPopoverProps) => {
  const { className, customStyles, children, displayType, showLogout = true, ...otherProps } = props

  const [open, setOpen] = useState(false)

  const buyPackage = useCallback((type: ServerDataTypes.PackagePlan['packetScene']) => {
    userService.doPay({
      from: 'userInfoPopover',
      wordPack: true,
      wordPackType: type,
      feature: 'other',
    })
  }, [])

  const remainData = useMemo(() => {
    const data: {
      title: React.ReactNode
      titleTip?: React.ReactNode
      items: {
        name: string
        tip?: React.ReactNode
        value: string
      }[]
    }[] = [
      {
        title: (
          <div className={classNames(style.title)}>
            <div className={classNames(style.left)}>
              <p className={classNames(style.name)}>模型</p>
            </div>

            {userService.vipVersion !== 'free' && (
              <div className={classNames(style.more)} onClick={() => buyPackage('ai_llm')}>
                获取更多 Token
              </div>
            )}
          </div>
        ),
        items: [
          {
            name: '基础模型',
            tip: <BaseModelTip styleCSS={{ marginLeft: '4px' }} />,
            value: '无限次',
          },
          {
            name: '高级模型',
            tip: (
              <AdvancedModelTip
                showToken={true}
                styleCSS={{ marginLeft: '4px' }}
                dailyFreeCount={userService.remainGiftProTokenCount}
                dailyFreeCountTotal={userService.totalGiftProTokenCount}
                vipTokenCount={userService.remainVipProTokenCount}
                vipTokenTotalCount={userService.totalVipProTokenCount}
                packageTokenCount={userService.remainBuyProTokenCount}
                packageTokenTotalCount={userService.totalBuyProTokenCount}
              />
            ),
            value: `${userService.remainProTokenCount.toLocaleString()} Token`,
          },
          {
            name: '顶级模型',
            tip: (
              <UltraModelTip
                styleCSS={{ marginLeft: '4px' }}
                showToken={true}
                dailyFreeCount={userService.remainGiftUltraTokenCount}
                dailyFreeCountTotal={userService.totalGiftUltraTokenCount}
                vipTokenCount={userService.remainVipUltraTokenCount}
                vipTokenTotalCount={userService.totalVipUltraTokenCount}
                packageTokenCount={userService.remainBuyUltraTokenCount}
                packageTokenTotalCount={userService.totalBuyUltraTokenCount}
              />
            ),
            value: `${userService.remainUltraTokenCount.toLocaleString()} Token`,
          },
        ],
      },
      {
        title: (
          <div className={classNames(style.title)}>
            <div className={classNames(style.left)}>
              <p className={classNames(style.name)}>PDF翻译</p>
            </div>

            {userService.vipVersion !== 'free' && (
              <div className={classNames(style.more)} onClick={() => buyPackage('pdf_pro')}>
                获取更多页数
              </div>
            )}
          </div>
        ),
        items: [
          {
            name: '分段翻译',
            value: `${userService.remainPdfProPageCount.toLocaleString()}页`,
            tip: (
              <PdfProTip
                showToken={true}
                styleCSS={{ marginLeft: '4px' }}
                dailyFreeCount={userService.remainGiftPdfProPageCount}
                dailyFreeCountTotal={userService.totalGiftPdfProPageCount}
                vipCount={userService.remainVipPdfProPageCount}
                vipTotalCount={userService.totalVipPdfProPageCount}
                packageCount={userService.remainBuyPdfProPageCount}
                packageCountTotal={userService.totalBuyPdfProPageCount}
              />
            ),
          },
          {
            name: '无损翻译',
            value: `${userService.remainPdfUltraPageCount.toLocaleString()}页`,
            tip: (
              <PdfUltraTip
                showToken={true}
                styleCSS={{ marginLeft: '4px' }}
                dailyFreeCount={userService.remainGiftPdfUltraPageCount}
                dailyFreeCountTotal={userService.totalGiftPdfUltraPageCount}
                vipCount={userService.remainVipPdfUltraPageCount}
                vipTotalCount={userService.totalVipPdfUltraPageCount}
                packageCount={userService.remainBuyPdfUltraPageCount}
                packageCountTotal={userService.totalBuyPdfUltraPageCount}
              />
            ),
          },
        ],
      },
      {
        title: (
          <div className={classNames(style.title)}>
            <div className={classNames(style.left)}>
              <p className={classNames(style.name)}>图片翻译</p>
              <ImageTip
                showToken={true}
                styleCSS={{ marginLeft: '4px' }}
                dailyFreeCount={userService.remainBuyImageTranslateCount}
                dailyFreeCountTotal={userService.totalBuyImageTranslateCount}
                packageCount={userService.remainVipImageTranslateCount}
                packageCountTotal={userService.totalVipImageTranslateCount}
              />
            </div>
          </div>
        ),
        items: [
          {
            name: '图片翻译',
            value: `共剩${userService.remainImageTranslateCount}张`,
          },
          {
            name: '截图翻译',
            value: `共剩${userService.remainImageTranslateCount}张`,
          },
        ],
      },
    ]

    return data
  }, [userService.userData])

  const onLogoutClick = useCallback(() => {
    userService.logout()
  }, [])

  if (!userService.userData) {
    return null
  }

  const {
    nickname,
    inviteCode,
    versionData: { version },
  } = userService.userData!

  const content = (
    <div className={classNames(style.userInfoPopover, className)} style={{ ...customStyles }}>
      <div className={classNames(style.user)}>
        <img className={classNames(style.avatar)} src={commonUtils.getDefaultAvatar()} />
        <div className={classNames(style.info)}>
          <p className={classNames(style.name)}>
            {nickname ? nickname : `会译${inviteCode.slice(0, 4).toUpperCase()}`}{' '}
          </p>
          <p className={classNames(style.id)}>
            ID: {inviteCode}
            <span
              className={classNames(style.copyIcon)}
              onClick={() => {
                toastController.success('复制成功')
                commonUtils.copyToClipboard(inviteCode)
              }}
            >
              <SvgIcon
                fontClass={'icon-line-global_Copy'}
                style={{
                  opacity: 0.5,
                  width: '12px',
                  height: '12px',
                }}
              />
            </span>
          </p>
        </div>

        <Link to='/dashboard/order' target='__blank'>
          <img
            className={classNames(style.orderIcon)}
            src={commonUtils.getStaticFile('icon__order.png')}
          />
        </Link>
      </div>

      <div className={classNames(style.content)}>
        <div className={classNames(style.top)}>
          <p className={classNames(style.version)}>
            <span className={classNames(style.versionTitle)}>套餐：</span>
            <span className={classNames(style.versionName)}>
              {userService.getVersionDisplayName(version)}
            </span>
          </p>

          {userService.vipVersion !== 'ultra' && (
            <UpgradeLinkBtn
              version={userService.vipVersion === 'free' ? 'max' : 'ultra'}
              text={
                userService.vipVersion === 'free'
                  ? '立即升级，畅享高级模型'
                  : '升级为Ultra，解锁顶级模型'
              }
              source='userInfoPopover'
              feature='other'
            />
          )}
        </div>

        {userService.currentVersionEndTime && (
          <div className={classNames(style.endTime)}>
            过期时间:{userService.currentVersionEndTime}
            <ExpiredTip styleCSS={{ marginLeft: '4px' }} />
          </div>
        )}

        {remainData.map((group, index) => {
          return (
            <div className={classNames(style.card)} key={index}>
              {group.title}
              {/* <div className={classNames(style.title)}>
                {group.title}
                {group.titleTip && (
                  <span className={classNames(style.titleTip)}>{group.titleTip}</span>
                )}
              </div> */}

              {group.items.map((item, index) => {
                return (
                  <div className={classNames(style.item)} key={index}>
                    <div className={classNames(style.name)}>
                      <img
                        className={classNames(style.icon)}
                        src={commonUtils.getStaticFile('func_item.png')}
                      />
                      <span className={classNames(style.text)}>{item.name}</span>
                      {item.tip && item.tip}
                    </div>
                    <p className={classNames(style.value)}>{item.value}</p>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      {showLogout && (
        <div className={classNames(style.logout)} onClick={onLogoutClick}>
          <img
            className={classNames(style.logoutIcon)}
            src={commonUtils.getStaticFile('icon__logout-v2.png')}
          />
          <p className={classNames(style.logoutText)}>退出登录</p>
        </div>
      )}
    </div>
  )

  if (displayType === 'normal') {
    return content
  }

  return (
    <Popover
      arrow={false}
      content={content}
      trigger={'click'}
      overlayInnerStyle={{
        padding: 0,
        boxShadow: 'unset',
        border: 'unset',
        background: 'unset',
        backgroundClip: 'unset',
      }}
      {...otherProps}
    >
      {children}
    </Popover>
  )
})
